import React from 'react';

import { NetworkStatus } from '@apollo/client';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, Card, H1, Icon, Separator } from '@betterment/samba-react';
import { ActivityError } from '@betterment/shared-components/src/components/activity/components/ActivityError';
import { FilterRowSkeleton } from '@betterment/shared-components/src/components/activity/components/FilterRowSkeleton';
import { NoResults } from '@betterment/shared-components/src/components/activity/components/NoResults';
import { safeDateString } from '@betterment/shared-components/src/components/activity/utils/dateUtils';
import { useFallbackKeys } from '@betterment/shared-components/src/hooks/useFallbackKeys';
import { useSearchParams } from 'react-router-dom';

import { FilterRow } from './components/FilterRow';
import { Pre2024ActivityBanner } from './components/Pre2024ActivityBanner';
import { Row } from './components/Row';
import { TableSkeleton } from './components/TableSkeleton';
import {
  ActivityItemsDocument,
  ActivityItemsQuery,
  ActivityItemsQueryVariables,
} from './graphql/query.activityItems.generated';
import { useFilterOptions } from './hooks/useFilterOptions';

import { ApplicationLayout } from '../Shared/layout/ApplicationLayout';

const INITIAL_ROW_COUNT = 50;
const FETCH_MORE_COUNT = 25;

const ActivityTableUIState = {
  filtersError: 'FILTERS_ERROR',
  itemsError: 'ITEMS_ERROR',
  filtersLoading: 'LOADING',
  itemsLoading: 'ITEMS_LOADING',
  loadingMore: 'LOADING_MORE',
  noResults: 'NO_RESULTS',
  complete: 'COMPLETE',
} as const;

export const ActivityPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const accountIds = searchParams.getAll('account_id');

  const {
    filterOptions,
    filters,
    setFilters,
    resetFilters,
    loading: isFiltersLoading,
    error: filterError,
    validationErrors: filterValidationErrors,
  } = useFilterOptions({ accountId: accountIds });
  const [hasFetchMoreErrors, setHasFetchMoreErrors] = React.useState(false);

  const isCustomTimeframeUnset =
    filters?.timeframe.dateRange.startDateInclusive === undefined ||
    filters?.timeframe.dateRange.endDateInclusive === undefined;

  const shouldSkipQuery = !filters || isCustomTimeframeUnset || !!Object.keys(filterValidationErrors ?? {}).length;

  const {
    data,
    networkStatus,
    error: itemsError,
    fetchMore,
  } = useGraphQLQuery<ActivityItemsQuery, ActivityItemsQueryVariables>(ActivityItemsDocument, {
    variables: {
      first: INITIAL_ROW_COUNT,
      filters: {
        accountIds: filters?.accountId,
        startDateInclusive: safeDateString(filters?.timeframe.dateRange.startDateInclusive),
        endDateInclusive: safeDateString(filters?.timeframe.dateRange.endDateInclusive),
        activityTypes: filters?.transactionType,
      },
    },
    notifyOnNetworkStatusChange: true,
    skip: shouldSkipQuery,
  });

  const pageInfo = data?.activityConnection.pageInfo;
  const items = useFallbackKeys(data?.activityConnection.edges.map(edge => edge.node) ?? []);

  const startDateInclusive = filters?.timeframe.dateRange.startDateInclusive;
  const isViewingPre2024Activity = startDateInclusive && startDateInclusive < new Date('2024-01-01');

  const UIState = (() => {
    const isFetchingInitialData = networkStatus === NetworkStatus.loading;
    const isRefetchingDueToFiltersChange = networkStatus === NetworkStatus.setVariables;
    const isRefetchingDueToMutation = networkStatus === NetworkStatus.refetch;
    const isFetchingMoreData = networkStatus === NetworkStatus.fetchMore;

    if (itemsError) return ActivityTableUIState.itemsError;
    if (filterError) return ActivityTableUIState.filtersError;
    if (isFiltersLoading) return ActivityTableUIState.filtersLoading;
    if (!data || isFetchingInitialData || isRefetchingDueToFiltersChange || isRefetchingDueToMutation)
      return ActivityTableUIState.itemsLoading;
    if (items.length === 0) return ActivityTableUIState.noResults;
    if (isFetchingMoreData) return ActivityTableUIState.loadingMore;

    return ActivityTableUIState.complete;
  })();

  const handleLoadMore = async () => {
    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) return;
    const { error: fetchMoreErrors } = await fetchMore({
      variables: { first: FETCH_MORE_COUNT, after: pageInfo.endCursor },
    });
    setHasFetchMoreErrors(!!fetchMoreErrors);
  };

  return (
    <ApplicationLayout pageTitle="Activity">
      <ApplicationLayout.Header>
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingY: { base: 'space.200', md: 'space.400' },
            paddingX: { base: 'space.300', md: 'space.0' },
          }}
        >
          <H1>Activity</H1>
        </Box>
      </ApplicationLayout.Header>
      <ApplicationLayout.Content>
        <Card css={{ width: '100%', gap: 'space.400', pb: 'space.400' }}>
          <Card.Header
            css={{ px: 'space.400', pt: 'space.400', display: 'flex', flexDirection: 'column', gap: 'space.400' }}
          >
            {(() => {
              switch (UIState) {
                case ActivityTableUIState.filtersLoading:
                  return <FilterRowSkeleton />;
                case ActivityTableUIState.filtersError:
                  return null;
                case ActivityTableUIState.itemsError:
                case ActivityTableUIState.itemsLoading:
                case ActivityTableUIState.noResults:
                case ActivityTableUIState.complete:
                case ActivityTableUIState.loadingMore:
                  return (
                    filterOptions &&
                    filters && (
                      <>
                        <FilterRow
                          filterOptions={filterOptions}
                          currentFilter={filters}
                          setFilters={setFilters}
                          resetFilters={resetFilters}
                          errors={filterValidationErrors}
                        />
                        {isViewingPre2024Activity && <Pre2024ActivityBanner />}
                      </>
                    )
                  );
                default: {
                  const exhaustiveCheck: never = UIState;
                  return exhaustiveCheck;
                }
              }
            })()}
          </Card.Header>
          <Card.Content>
            {(() => {
              switch (UIState) {
                case ActivityTableUIState.filtersError:
                case ActivityTableUIState.itemsError:
                  return <ActivityError />;
                case ActivityTableUIState.filtersLoading:
                case ActivityTableUIState.itemsLoading:
                  return (
                    <>
                      <Separator css={{ fullWidthWithInset: 'space.400' }} />
                      <TableSkeleton />
                    </>
                  );
                case ActivityTableUIState.noResults:
                  return <NoResults title="No activity found" description="Adjust your filters to find transactions" />;
                case ActivityTableUIState.complete:
                case ActivityTableUIState.loadingMore:
                  return (
                    <>
                      {items.map(({ key, item }) => (
                        <Row key={key} item={item} />
                      ))}
                      <Separator css={{ fullWidthWithInset: 'space.400' }} />
                    </>
                  );
                default: {
                  const exhaustiveCheck: never = UIState;
                  return exhaustiveCheck;
                }
              }
            })()}
          </Card.Content>
          {(() => {
            switch (UIState) {
              case ActivityTableUIState.filtersError:
              case ActivityTableUIState.itemsError:
              case ActivityTableUIState.filtersLoading:
              case ActivityTableUIState.itemsLoading:
              case ActivityTableUIState.noResults:
                return null;
              case ActivityTableUIState.complete:
              case ActivityTableUIState.loadingMore:
                if (!pageInfo?.hasNextPage) {
                  return null;
                }
                return (
                  <Card.Footer css={{ alignItems: 'left', pl: 'space.400' }}>
                    {hasFetchMoreErrors && (
                      <Box css={{ mb: 'space.200', display: 'flex', alignItems: 'center', gap: 'space.100' }}>
                        <Icon name="ExclamationCircle" size="small" />
                        <p>Something went wrong, try again.</p>
                      </Box>
                    )}
                    <Button
                      variant="solidSecondary"
                      loading={UIState === ActivityTableUIState.loadingMore}
                      onClick={handleLoadMore}
                    >
                      Load more
                    </Button>
                  </Card.Footer>
                );
              default: {
                const exhaustiveCheck: never = UIState;
                return exhaustiveCheck;
              }
            }
          })()}
        </Card>
      </ApplicationLayout.Content>
    </ApplicationLayout>
  );
};
